// Global floating voice widget — appears on every page after 3 s.
// A/B tests two 15-s intros of Prof Lionel (calm vs energetic),
// tracking impression / play / complete / cta_click / dismiss to the backend.
//
// Behaviour:
//   1. Mounts on every route via App.js (skips /admin/*).
//   2. Waits 3 s after the page settles.
//   3. Assigns a stable variant (a/b) per browser via localStorage.
//   4. HEAD-probes /audio/lionel-intro-15s-{a|b}.mp3; only mounts if the MP3 is real.
//   5. Persists dismissed state (24 h TTL) and reports events to /api/ab/voice-widget.

import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { AUTHOR } from "@/lib/authorSchema";
import { Play, Pause, X, ArrowRight } from "lucide-react";

const AUDIO_URL = (v, suffix = "") => `/audio/lionel-intro-15s-${v}${suffix}.mp3`;
const DISMISS_KEY = "lionel_intro_dismissed_at";
const VARIANT_KEY = "lionel_intro_variant";
const SESSION_KEY = "lionel_intro_session";
const IMPRESSION_KEY = "lionel_intro_impression_at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 h
const IMPRESSION_TTL_MS = 30 * 60 * 1000; // dedup impression counter per 30 min
const MOUNT_DELAY_MS = 3000;

const T = {
  pt: {
    label: "1 min com Prof Lionel",
    hookA: "Ouça meu convite para sua 1ª aula grátis.",
    hookB: "Bora falar francês parisiense de verdade?",
    cta: "Quero minha aula grátis",
  },
  en: {
    label: "1 min with Prof Lionel",
    hookA: "Listen to my invite for your 1st free class.",
    hookB: "Ready to speak real Parisian French?",
    cta: "Get my free class",
  },
  fr: {
    label: "1 min avec Prof Lionel",
    hookA: "Écoute mon invitation à ton 1er cours offert.",
    hookB: "Prêt à parler le vrai français parisien ?",
    cta: "Je veux mon cours offert",
  },
};

function pickVariant() {
  try {
    const cached = localStorage.getItem(VARIANT_KEY);
    if (cached === "a" || cached === "b") return cached;
    const v = Math.random() < 0.5 ? "a" : "b";
    localStorage.setItem(VARIANT_KEY, v);
    return v;
  } catch (_) {
    return Math.random() < 0.5 ? "a" : "b";
  }
}

function pickSession() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch (_) {
    return "anon-" + Math.random().toString(36).slice(2, 10);
  }
}

async function track(event, variant, session) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    // fire-and-forget; keepalive lets it survive route changes
    fetch(`${base}/api/ab/voice-widget`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, variant, session_id: session }),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {}
}

export default function VoiceIntroWidget() {
  const { lang } = useLanguage();
  const location = useLocation();
  const t = T[lang] || T.pt;
  const [mounted, setMounted] = useState(false);
  const [variant, setVariant] = useState("a");
  const [session, setSession] = useState("");
  const [audioSrc, setAudioSrc] = useState(null);
  const hasAudio = !!audioSrc;
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const skip = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (skip) return;
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (at && Date.now() - at < DISMISS_TTL_MS) return;
    } catch (_) {}
    setVariant(pickVariant());
    setSession(pickSession());
    const timer = setTimeout(() => setMounted(true), MOUNT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [skip, location.pathname]);

  useEffect(() => {
    if (!mounted) return;
    let cancelled = false;
    const isAudio = (url) =>
      fetch(url, { method: "HEAD" })
        .then((r) => r.ok && (r.headers.get("content-type") || "").toLowerCase().startsWith("audio/"))
        .catch(() => false);
    const suffix = lang === "fr" ? "-fr" : lang === "en" ? "-en" : "";
    const candidates = suffix ? [AUDIO_URL(variant, suffix), AUDIO_URL(variant)] : [AUDIO_URL(variant)];
    (async () => {
      for (const url of candidates) {
        if (await isAudio(url)) return !cancelled && setAudioSrc(url);
      }
      if (!cancelled) setAudioSrc(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [mounted, variant, lang]);

  // Fire an impression once the widget is actually visible — dedup per 30 min.
  useEffect(() => {
    if (!mounted || !hasAudio || !session) return;
    try {
      const at = Number(localStorage.getItem(IMPRESSION_KEY) || 0);
      if (at && Date.now() - at < IMPRESSION_TTL_MS) return;
      localStorage.setItem(IMPRESSION_KEY, String(Date.now()));
    } catch (_) {}
    track("impression", variant, session);
  }, [mounted, hasAudio, variant, session]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    if (audioRef.current) audioRef.current.pause();
    track("dismiss", variant, session);
    setMounted(false);
  };

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
      track("play", variant, session);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const onCta = () => {
    track("cta_click", variant, session);
    dismiss();
  };

  if (skip || !mounted || !hasAudio) return null;

  const hook = variant === "b" ? t.hookB : t.hookA;

  return (
    <div
      role="dialog"
      aria-label={t.label}
      data-testid="voice-intro-widget"
      data-variant={variant}
      data-floating="true"
      className="fixed bottom-24 right-4 z-40 w-[min(340px,calc(100vw-2rem))] rounded-2xl border border-[#e5e3db] bg-white shadow-[0_8px_28px_-8px_rgba(30,42,90,0.25)] p-4 sm:p-5 animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <button
        onClick={dismiss}
        aria-label="close"
        data-testid="voice-intro-close"
        className="absolute top-2 right-2 h-7 w-7 inline-flex items-center justify-center rounded-full text-[#5c5c5c] hover:bg-[#f0efea] hover:text-[#1e2a5a] transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-start gap-3">
        <img
          src={AUTHOR.image}
          alt={AUTHOR.name}
          width="44"
          height="44"
          loading="lazy"
          className="w-11 h-11 rounded-full object-cover shrink-0 border border-[#e5e3db]"
        />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#d9381e] font-medium">
            {t.label}
          </p>
          <p className="mt-0.5 text-sm text-[#1e2a5a] leading-snug">{hook}</p>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button
          onClick={toggle}
          data-testid="voice-intro-toggle"
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#d9381e] text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors"
          aria-label={playing ? "pause" : "play"}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-[2px]" />}
        </button>
        <div className="flex-1 h-1.5 rounded-full bg-[#e5e3db] overflow-hidden">
          <div
            className="h-full bg-[#d9381e] transition-all"
            style={{ width: playing ? "100%" : "0%", transitionDuration: playing ? "15s" : "0.3s" }}
          />
        </div>
      </div>
      <Link
        to="/aula-gratis"
        onClick={onCta}
        data-testid="voice-intro-cta"
        className="mt-3 group inline-flex items-center gap-1.5 text-sm text-[#1e2a5a] hover:text-[#d9381e] transition-colors"
      >
        {t.cta}
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
      </Link>
      <audio
        ref={audioRef}
        src={audioSrc || undefined}
        preload="none"
        onEnded={() => {
          setPlaying(false);
          track("complete", variant, session);
        }}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
    </div>
  );
}
